import * as regexp from '../../constants/regExp'
import {isNumber, isString} from './validationUtils'

class Validator {
  value: any
  errors: string[]
  isOptional: boolean

  constructor(value: any) {
    this.value = value
    this.errors = []
    this.isOptional = false
  }

  get error(): string {
    return this.errors[0] || ''
  }

  worker = ({errorMessage, isValid}: {errorMessage: string; isValid: boolean}): Validator => {
    if (!isValid) {
      this.errors.push(errorMessage)
    }
    return this
  }

  string = (errorMessage = 'Provided value should be a string'): Validator => {
    return this.worker({
      errorMessage,
      isValid: this.isOptional && !this.value ? true : isString(this.value),
    })
  }

  number = (errorMessage = 'Provided value should be a number'): Validator => {
    return this.worker({
      errorMessage,
      isValid: this.isOptional && !isNumber(this.value) ? true : isNumber(this.value),
    })
  }

  required = (errorMessage = 'Required field'): Validator => {
    return this.worker({
      errorMessage,
      isValid: !(this.value === undefined || this.value === '' || this.value === false || (Array.isArray(this.value) && !this.value.length)),
    })
  }

  maxLength = (length: number, errorMessage?: string): Validator => {
    const message = errorMessage || `Up to ${length} options are allowed`
    return this.worker({
      errorMessage: message,
      isValid: this.isOptional && !this.value ? true : this.value && this.value.length <= length,
    })
  }

  minLength = (length: number, errorMessage?: string): Validator => {
    const message = errorMessage || `At least ${length} option is required `
    return this.worker({
      errorMessage: message,
      isValid: this.isOptional && !this.value ? true : this.value && this.value.length >= length,
    })
  }

  email = (errorMessage = 'Enter a valid email address'): Validator => {
    return this.worker({
      errorMessage,
      isValid: this.isOptional && !this.value ? true : regexp.emailRegexp.test(this.value),
    })
  }

  optional = (): Validator => {
    this.isOptional = true
    return this
  }

  url = (errorMessage = 'Please enter a valid url address'): Validator => {
    return this.worker({
      errorMessage,
      isValid: this.isOptional && !this.value ? true : regexp.urlRegexp.test(this.value),
    })
  }

  password = (errorMessage = 'Password cannot begin or end with a space'): Validator => {
    return this.worker({
      errorMessage,
      isValid: this.isOptional && !this.value ? true : regexp.passwordRegexp.test(this.value),
    })
  }

  range = (range: [number, number], errorMessage: string): Validator => {
    const message = errorMessage || `Provided value should be in range between ${range[0]} and ${range[1]}`
    return this.worker({
      errorMessage: message,
      isValid: this.isOptional && !this.value ? true : this.value >= range[0] && this.value <= range[1],
    })
  }

  positive = (errorMessage = 'Should be positive'): Validator => {
    return this.worker({
      errorMessage,
      isValid: !!this.value,
    })
  }

  custom = (method: () => {errorMessage: string; isValid: boolean}, args: any): Validator => {
    return this.worker(method.apply(this, args))
  }
}

const validationInterface = {
  rules: (config: {[key: string]: {error: string}}) =>
    Object.keys(config).reduce((prev, curr) => ({...prev, ...{...(config[curr].error ? {[curr]: config[curr].error} : {})}}), {}),
  validate: (value: any) => new Validator(value),
}

export {validationInterface as validator}
